import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ExternalLink, Info, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import DescriptionCell from "@/pages/DescriptionCell";

const monthLongLabel = (period) => {
  if (!period) return "";
  const [y, m] = period.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
};
const fmtDate = (s) => {
  if (!s) return "-";
  return new Date(s + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};
const fmt = (n) => (n != null ? parseFloat(n).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "-");
const shortDesc = (d, maxLen = 50) => {
  if (!d) return "";
  if (d.length <= maxLen) return d;
  return d.substring(0, maxLen) + "...";
};

/**
 * Renders unpaid orders grouped by month.
 * Props:
 *  - data: { months:[{period, paid, total, orders, members_status?}], total_pending }
 *  - viewer: optional {member_id, name, color}  — when present, shows viewer_amount and per-row info icon for split note
 *  - onMarkMonth(period, memberId?, paid:bool): called to mark/unmark.  memberId=null means viewer or all.
 *  - markingKey: string identifying currently-saving cell
 *  - canMark: boolean (false on public share)
 *  - splittingEnabled: bool
 *  - compact: smaller spacing for shared/mobile view
 */
const PendingOrdersList = ({ data, viewer, onMarkMonth, markingKey, canMark, splittingEnabled, compact = false }) => {
  if (!data) return null;
  const isViewer = !!viewer;
  const { months, total_pending } = data;

  if (!months?.length) {
    return (
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-12 text-center" data-testid="pending-empty">
        <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
        <p className="text-stone-700 text-sm font-semibold">All clear</p>
        <p className="text-stone-400 text-xs mt-0.5">No pending bills{isViewer ? ` for ${viewer.name}` : ""}.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pending-orders-list">
      {total_pending > 0 ? (
        <div className={`flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-4 ${compact ? "py-2" : "py-3"}`} data-testid="pending-grand-total">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-xs sm:text-sm font-medium text-red-700">
              {isViewer ? `${viewer.name}'s pending bills` : "Pending bills"} · {months.filter(mo => !mo.paid).length} month{months.filter(mo => !mo.paid).length === 1 ? "" : "s"}
            </span>
          </div>
          <span className="text-base sm:text-lg font-heading font-extrabold text-red-700 tabular-nums">₹{fmt(total_pending)}</span>
        </div>
      ) : (
        <div className={`flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-4 ${compact ? "py-2" : "py-3"}`} data-testid="pending-grand-total">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-xs sm:text-sm font-medium text-emerald-700">
              {isViewer ? `${viewer.name} — all clear` : "All clear"}
            </span>
          </div>
        </div>
      )}

      {months.map((mo) => (
        <MonthBlock
          key={mo.period}
          month={mo}
          viewer={viewer}
          onMarkMonth={onMarkMonth}
          markingKey={markingKey}
          canMark={canMark}
          splittingEnabled={splittingEnabled}
          compact={compact}
        />
      ))}
    </div>
  );
};

const MonthBlock = ({ month, viewer, onMarkMonth, markingKey, canMark, splittingEnabled, compact }) => {
  const isViewer = !!viewer;
  const orders = month.orders || [];
  const padCls = compact ? "px-3 py-2" : "px-4 py-3";
  const isPaid = !!month.paid;

  return (
    <div className={`rounded-2xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] overflow-hidden ${isPaid ? "bg-emerald-50/30" : "bg-white"}`} data-testid={`pending-month-${month.period}`}>
      {/* Month header */}
      <div className={`flex flex-wrap items-center justify-between gap-2 ${padCls} ${isPaid ? "bg-emerald-50/60 border-emerald-100" : "bg-stone-50/60 border-stone-100"} border-b`}>
        <div className="flex items-center gap-2">
          {isPaid ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
          <span className={`text-sm font-heading font-extrabold ${isPaid ? "text-emerald-800" : "text-stone-900"}`}>{monthLongLabel(month.period)}</span>
          <span className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{orders.length} bill{orders.length > 1 ? "s" : ""}</span>
          {isPaid && <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">· paid</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-heading font-extrabold tabular-nums ${isPaid ? "text-emerald-700 line-through" : "text-red-600"}`}>₹{fmt(month.total)}</span>
          {canMark && isViewer && (
            isPaid ? (
              <button
                onClick={() => onMarkMonth(month.period, viewer.member_id, false)}
                disabled={markingKey === `${viewer.member_id}_${month.period}`}
                className="px-2.5 py-1 text-[10px] font-semibold rounded-full text-stone-600 bg-white border border-stone-300 hover:bg-stone-50 disabled:opacity-50 transition flex items-center gap-1"
                data-testid={`undo-month-${month.period}`}
              >
                {markingKey === `${viewer.member_id}_${month.period}` ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Undo
              </button>
            ) : (
              <button
                onClick={() => onMarkMonth(month.period, viewer.member_id, true)}
                disabled={markingKey === `${viewer.member_id}_${month.period}`}
                className="px-2.5 py-1 text-[10px] font-semibold rounded-full text-white bg-gradient-to-r from-[#4F6F52] to-[#5a7a5e] hover:from-[#3f5942] hover:to-[#4F6F52] disabled:opacity-50 transition flex items-center gap-1"
                data-testid={`mark-month-paid-${month.period}`}
              >
                {markingKey === `${viewer.member_id}_${month.period}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Mark paid
              </button>
            )
          )}
        </div>
      </div>

      {/* Per-member chips when not viewer-scoped */}
      {!isViewer && splittingEnabled && (month.members_status || []).length > 0 && canMark && (
        <div className={`${padCls} flex flex-wrap gap-1.5 border-b border-stone-100`}>
          {month.members_status.map((ms) => {
            const k = `${ms.member_id}_${month.period}`;
            const busy = markingKey === k;
            return (
              <button
                key={ms.member_id}
                onClick={() => onMarkMonth(month.period, ms.member_id, !ms.paid)}
                disabled={busy}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold transition border ${
                  ms.paid
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                } ${busy ? "opacity-50" : ""}`}
                title={ms.paid ? `${ms.name} paid · click to undo` : `${ms.name} unpaid · click to mark paid`}
                data-testid={`pending-member-chip-${ms.member_id}-${month.period}`}
              >
                {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : ms.paid ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
                <span className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-white text-[8px] font-bold" style={{ background: ms.color }}>{ms.name[0]?.toUpperCase()}</span>
                {ms.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Orders list */}
      <div>
        {/* Table — copied EXACTLY from Monthly tab (HouseholdDashboard) styling for visual parity */}
        <table className="w-full table-fixed" data-testid={`pending-orders-table-${month.period}`}>
          <thead>
            <tr className="border-b border-stone-100">
              <th className="w-[50px] sm:w-[72px] px-2 sm:px-4 py-2 sm:py-3 text-left text-[9px] sm:text-[10px] font-bold text-stone-400 uppercase tracking-wider sm:tracking-widest">Date</th>
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-[9px] sm:text-[10px] font-bold text-stone-400 uppercase tracking-wider sm:tracking-widest">Item</th>
              <th className="w-[62px] sm:w-[90px] px-1.5 sm:px-4 py-2 sm:py-3 text-right text-[9px] sm:text-[10px] font-bold text-stone-400 uppercase tracking-wider sm:tracking-widest">{isViewer ? "Bill" : "Amt"}</th>
              <th className="w-[62px] sm:w-[90px] px-1.5 sm:px-4 py-2 sm:py-3 text-right text-[9px] sm:text-[10px] font-bold text-stone-400 uppercase tracking-wider sm:tracking-widest">Ded.</th>
              <th className="w-[68px] sm:w-[90px] px-1.5 sm:px-4 py-2 sm:py-3 text-right text-[9px] sm:text-[10px] font-bold text-stone-400 uppercase tracking-wider sm:tracking-widest">{isViewer ? "Owe" : "Final"}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, idx) => (
              <tr key={o.log_id || idx} className={`border-b border-stone-50 hover:bg-stone-50/60 transition ${idx % 2 !== 0 ? "bg-stone-50/30" : ""}`}>
                <td className="px-2 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-sm text-stone-700 font-medium align-top">{fmtDate(o.date)}</td>
                <td className="px-2 sm:px-4 py-2 sm:py-3 truncate align-top">
                  <div className="flex items-center gap-1">
                    <p className="text-[10px] sm:text-sm font-semibold text-stone-800 truncate">{o.vendor_name || o.category || "-"}</p>
                    {o.receipt_link && (
                      <a href={o.receipt_link} target="_blank" rel="noopener noreferrer" className="text-[#4F6F52] hover:text-[#3f5942] flex-shrink-0">
                        <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      </a>
                    )}
                  </div>
                  <DescriptionCell description={o.description} className="text-[9px] sm:text-[11px] leading-tight" mobileMaxLen={32} />
                  {!isViewer && splittingEnabled && (o.member_shares || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {o.member_shares.filter((ms) => ms.amount > 0).map((ms) => (
                        <span key={ms.member_id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-semibold" style={{ background: `${ms.color}20`, color: ms.color }}>
                          {ms.name} ₹{fmt(ms.amount)}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-1.5 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-sm text-stone-700 text-right font-medium tabular-nums align-top">{o.amount ? `₹${fmt(o.amount)}` : "-"}</td>
                <td className="px-1.5 sm:px-4 py-2 sm:py-3 text-right align-top">
                  <span className={`text-[10px] sm:text-sm tabular-nums inline-flex items-center gap-0.5 sm:gap-1 ${o.deduction > 0 ? "text-red-600 font-semibold" : "text-stone-300"}`}>
                    {o.deduction > 0 ? `-₹${fmt(o.deduction)}` : "-"}
                    {o.notes && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className={`p-0.5 rounded-full transition ${o.deduction > 0 ? "hover:bg-red-100 text-red-400 hover:text-red-600" : "hover:bg-blue-100 text-blue-400 hover:text-blue-600"}`}>
                            <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="top" className="w-auto max-w-[240px] p-3 rounded-xl text-xs text-stone-600">{o.notes}</PopoverContent>
                      </Popover>
                    )}
                  </span>
                </td>
                <td className="px-1.5 sm:px-4 py-2 sm:py-3 text-[10px] sm:text-sm text-stone-900 text-right font-bold tabular-nums align-top">
                  <span className="inline-flex items-center gap-0.5 sm:gap-1 justify-end">
                    ₹{fmt(isViewer ? o.viewer_amount : o.final_amount)}
                    {isViewer && o.split_note && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="p-0.5 rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition" data-testid={`pending-split-info-${month.period}-${idx}`}>
                            <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="top" className="w-auto max-w-[240px] p-2.5 rounded-xl text-[11px] text-stone-600 italic leading-snug">
                          {o.split_note}
                        </PopoverContent>
                      </Popover>
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PendingOrdersList;
